// ─── WorkRoamer™ Beta Operations Center™ — Data Layer ────────────────────────
//
// Build: 2.44.0 — Sprint 098 (major enhancement)
//
// New in 2.44.0:
//   • Duplicate detection (Jaccard token similarity)
//   • Automatic severity scoring (critical/high/medium/low)
//   • 9-state developer workflow with full timeline history
//   • Build version tracking (reported/fixed/verified)
//   • Operator profile attachment
//   • Advanced analytics (15 new metrics)
//   • Regression detection
//   • Internal developer notes
//   • Attachment tracking
//   • 8 ChatGPT export formats
//   • Daily + Weekly summary generation

export const BETA_OPS_BUILD = '2.47.0';
const STORAGE_KEY = 'nomad:beta-ops:v1';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FeedbackType     = 'bug' | 'feature' | 'improvement' | 'question' | 'positive' | 'rating';
export type FeedbackStatus   = 'new' | 'reviewed' | 'actioned' | 'closed'; // legacy compat
export type FeedbackSeverity = 'critical' | 'high' | 'medium' | 'low';
export type FeedbackDevStatus =
  | 'new' | 'needs-investigation' | 'assigned' | 'in-progress'
  | 'cannot-reproduce' | 'fixed' | 'verified' | 'released'
  | 'deferred' | 'rejected' | 'closed';

export interface TimelineEvent {
  id:        string;
  devStatus: FeedbackDevStatus;
  timestamp: string;
  note?:     string;
}

export interface InternalNote {
  id:        string;
  content:   string;
  timestamp: string;
}

export interface FeedbackAttachment {
  id:        string;
  kind:      'screenshot' | 'recording' | 'log' | 'json' | 'stack-trace' | 'diagnostic';
  name:      string;
  url:       string;
  timestamp: string;
}

export interface OperatorProfile {
  operatorId?:      string;
  operatorName?:    string;
  country?:         string;
  timezone?:        string;
  sessionDuration?: string;
  userTier?:        string;
  betaCohort?:      string;
}

export interface BetaFeedback {
  id:               string;
  type:             FeedbackType;
  title:            string;
  description:      string;
  rating?:          number;           // 1–5 for type='rating'
  // auto-captured context
  buildNumber:      string;
  device:           string;
  browser:          string;
  os:               string;
  screenResolution: string;
  currentRoute:     string;
  currentModule:    string;
  timestamp:        string;
  platformVersion:  string;
  screenshotUrl?:   string;
  // v1 linkage (preserved)
  bugId?:           string;
  status:           FeedbackStatus;
  // Sprint 098 fields
  severity:         FeedbackSeverity;
  devStatus:        FeedbackDevStatus;
  timeline:         TimelineEvent[];
  internalNotes:    InternalNote[];
  attachments:      FeedbackAttachment[];
  operatorProfile:  OperatorProfile;
  reportedInBuild:  string;
  fixedInBuild?:    string;
  verifiedInBuild?: string;
  releasedInBuild?: string;
  duplicateGroupId?: string;
  occurrenceCount:  number;
  popularity:       number;           // upvote count for feature requests
  isRegression:     boolean;
  assignedTo?:      string;
}

export interface BetaAnalytics {
  // v1 fields preserved
  totalFeedback:            number;
  byType:                   Record<FeedbackType, number>;
  averageRating:            number | null;
  openBugsCount:            number;
  crashReports:             number;
  uiIssues:                 number;
  aiIssues:                 number;
  storageIssues:            number;
  authIssues:               number;
  topComplaints:            string[];
  topFeatureRequests:       string[];
  mostProblematicModules:   string[];
  mostStableModules:        string[];
  crashRate:                number;
  aiSuccessRate:            number;
  storageSuccessRate:       number;
  buildVersionDistribution: Record<string, number>;
  uniqueTesters:            number;
  dailyActiveTesters:       number;
  recentFeedbackCount:      number;
  platformHealthScore:      number;
  // Sprint 098 new fields
  criticalCount:            number;
  highCount:                number;
  mediumCount:              number;
  lowCount:                 number;
  avgSeverityScore:         number;   // 1=low 4=critical
  openIssuesCount:          number;
  closedIssuesCount:        number;
  regressionCount:          number;
  duplicateGroupCount:      number;
  avgResolutionTimeHours:   number | null;
  crashFreeSessionsPct:     number;
  reportsByBrowser:         Record<string, number>;
  reportsByDevice:          Record<string, number>;
  reportsByModule:          Record<string, number>;
  topFeaturesByPopularity:  Array<{ title: string; popularity: number }>;
  bySeverity:               Record<FeedbackSeverity, number>;
  byDevStatus:              Record<FeedbackDevStatus, number>;
  avgResolutionDays:        number | null;
}

interface Store {
  feedback:    BetaFeedback[];
  lastUpdated: string;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function load(): Store {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) return JSON.parse(raw) as Store;
  } catch { /* ignore */ }
  return { feedback: [], lastUpdated: new Date().toISOString() };
}

function persist(store: Store): void {
  try {
    store.lastUpdated = new Date().toISOString();
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
  } catch { /* quota safe */ }
}

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function parseBrowser(ua: string): string {
  if (ua.includes('Firefox/'))  return 'Firefox';
  if (ua.includes('Edg/'))      return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
  if (ua.includes('Chrome/'))   return 'Chrome';
  if (ua.includes('Safari/'))   return 'Safari';
  return 'Unknown';
}

function parseOS(ua: string): string {
  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS X'))   return 'macOS';
  if (ua.includes('Android'))    return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Linux'))      return 'Linux';
  return 'Unknown';
}

function parseDevice(ua: string): string {
  if (/Mobi|Android/i.test(ua)) return 'Mobile';
  if (/iPad|Tablet/i.test(ua))  return 'Tablet';
  return 'Desktop';
}

const ROUTE_MODULE_MAP: [string, string][] = [
  ['/home',          'Mission Control'],
  ['/dashboard',     'Dashboard'],
  ['/opportunities', 'Opportunities'],
  ['/companies',     'Companies'],
  ['/income',        'Income'],
  ['/timer',         'Timer'],
  ['/career',        'Career'],
  ['/academy',       'Academy'],
  ['/passport',      'Passport'],
  ['/wallet',        'Wallet'],
  ['/stay',          'Stay'],
  ['/connect',       'Connect'],
  ['/health',        'Health'],
  ['/marketplace',   'Marketplace'],
  ['/learn',         'Vault'],
  ['/backpack',      'Backpack'],
  ['/settings',      'Settings'],
  ['/profile',       'Profile'],
  ['/dev',           'Dev Tools'],
  ['/occ',           'Control Center'],
];

export function routeToModule(route: string): string {
  for (const [prefix, name] of ROUTE_MODULE_MAP) {
    if (route.startsWith(prefix)) return name;
  }
  return 'Platform';
}

// ─── Severity auto-scorer ─────────────────────────────────────────────────────

const CRITICAL_KEYWORDS = ['crash', 'data loss', 'corrupted', 'login fail', 'can\'t log', 'cannot log', 'payment', 'lost data', 'broken auth', 'database', 'down', 'error 500', 'white screen', 'blank screen'];
const HIGH_KEYWORDS     = ['broken', 'blocked', 'doesn\'t work', 'not working', 'major', 'workflow', 'cannot complete', 'stuck', 'hangs', 'freezes', 'infinite loop'];
const MEDIUM_KEYWORDS   = ['incorrect', 'wrong', 'unexpected', 'visual', 'layout', 'display', 'missing', 'slow'];

export function autoScoreSeverity(type: FeedbackType, title: string, description: string): FeedbackSeverity {
  if (type !== 'bug' && type !== 'improvement') return 'low';
  const text = (title + ' ' + description).toLowerCase();
  if (CRITICAL_KEYWORDS.some(k => text.includes(k))) return 'critical';
  if (HIGH_KEYWORDS.some(k => text.includes(k)))     return 'high';
  if (MEDIUM_KEYWORDS.some(k => text.includes(k)))   return 'medium';
  if (type === 'improvement')                         return 'low';
  return 'medium'; // default for bugs
}

// ─── Duplicate detection ──────────────────────────────────────────────────────

function tokenise(s: string): Set<string> {
  return new Set(s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(t => t.length > 2));
}

function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1;
  const intersection = new Set([...a].filter(t => b.has(t)));
  const union = new Set([...a, ...b]);
  return union.size === 0 ? 0 : intersection.size / union.size;
}

function isSimilar(a: BetaFeedback, b: BetaFeedback): boolean {
  if (a.type !== b.type) return false;
  if (a.currentModule !== b.currentModule && a.currentRoute !== b.currentRoute) {
    // Different page entirely — only group if title is very similar
    const titleSim = jaccardSimilarity(tokenise(a.title), tokenise(b.title));
    return titleSim > 0.7;
  }
  const titleSim = jaccardSimilarity(tokenise(a.title), tokenise(b.title));
  if (titleSim > 0.55) return true;
  const descSim  = jaccardSimilarity(tokenise(a.description), tokenise(b.description));
  return descSim > 0.45;
}

export function detectAndGroupDuplicates(all: BetaFeedback[]): BetaFeedback[] {
  const bugs  = all.filter(f => f.type === 'bug');
  const rest  = all.filter(f => f.type !== 'bug');
  const groups: Map<string, string> = new Map(); // id → groupId

  for (let i = 0; i < bugs.length; i++) {
    for (let j = i + 1; j < bugs.length; j++) {
      if (isSimilar(bugs[i], bugs[j])) {
        const gid = groups.get(bugs[i].id) ?? groups.get(bugs[j].id) ?? `dup-${uid()}`;
        groups.set(bugs[i].id, gid);
        groups.set(bugs[j].id, gid);
      }
    }
  }

  // Count occurrences per group
  const groupCounts: Record<string, number> = {};
  for (const gid of groups.values()) {
    groupCounts[gid] = (groupCounts[gid] ?? 0) + 1;
  }

  const enriched = bugs.map(f => ({
    ...f,
    duplicateGroupId: groups.get(f.id),
    occurrenceCount:  groups.get(f.id) ? (groupCounts[groups.get(f.id)!] ?? 1) : 1,
  }));

  return [...enriched, ...rest];
}

// ─── Regression detection ─────────────────────────────────────────────────────

export function detectRegressions(all: BetaFeedback[]): BetaFeedback[] {
  const fixedTitles = new Set(
    all
      .filter(f => f.devStatus === 'fixed' || f.devStatus === 'released' || f.devStatus === 'verified')
      .map(f => f.title.toLowerCase().trim()),
  );

  return all.map(f => {
    if (f.type !== 'bug') return f;
    if (f.devStatus === 'fixed' || f.devStatus === 'released' || f.devStatus === 'verified' || f.devStatus === 'closed') return f;
    const isRegression = fixedTitles.has(f.title.toLowerCase().trim());
    return isRegression ? { ...f, isRegression: true } : f;
  });
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function captureContext(): Omit<BetaFeedback,
  'id' | 'type' | 'title' | 'description' | 'status' | 'rating' | 'screenshotUrl' | 'bugId'
  | 'severity' | 'devStatus' | 'timeline' | 'internalNotes' | 'attachments'
  | 'operatorProfile' | 'reportedInBuild' | 'occurrenceCount' | 'popularity' | 'isRegression'
> {
  const ua    = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const route = typeof window   !== 'undefined' ? window.location.pathname : '/';
  return {
    buildNumber:      BETA_OPS_BUILD,
    device:           parseDevice(ua),
    browser:          parseBrowser(ua),
    os:               parseOS(ua),
    screenResolution: typeof window !== 'undefined' ? `${window.screen.width}×${window.screen.height}` : 'unknown',
    currentRoute:     route,
    currentModule:    routeToModule(route),
    timestamp:        new Date().toISOString(),
    platformVersion:  BETA_OPS_BUILD,
  };
}

export function submitFeedback(
  input: Omit<BetaFeedback, 'id' | 'timestamp' | 'status' | 'devStatus' | 'timeline' | 'internalNotes' | 'attachments' | 'isRegression'>,
  bugId?: string,
): BetaFeedback {
  const store   = load();
  const severity = input.severity ?? autoScoreSeverity(input.type, input.title, input.description);
  const entry: BetaFeedback = {
    ...input,
    id:              `bfb-${uid()}`,
    timestamp:       new Date().toISOString(),
    status:          'new',
    devStatus:       'new',
    severity,
    timeline:        [{ id: uid(), devStatus: 'new', timestamp: new Date().toISOString(), note: 'Submitted by operator' }],
    internalNotes:   [],
    attachments:     [],
    operatorProfile: input.operatorProfile ?? {},
    reportedInBuild: BETA_OPS_BUILD,
    occurrenceCount: input.occurrenceCount ?? 1,
    popularity:      input.popularity ?? 0,
    isRegression:    false,
    bugId,
  };
  store.feedback.push(entry);
  persist(store);
  return entry;
}

export function getAllFeedback(): BetaFeedback[] {
  const raw = [...load().feedback].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
  // Run enrichment pipelines
  const withDupes = detectAndGroupDuplicates(raw);
  return detectRegressions(withDupes);
}

export function updateDevStatus(
  id: string,
  devStatus: FeedbackDevStatus,
  note?: string,
  assignedTo?: string,
): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === id);
  if (idx === -1) return;
  const f   = store.feedback[idx];
  const now = new Date().toISOString();

  const patch: Partial<BetaFeedback> = {
    devStatus,
    status:   devStatus === 'closed' || devStatus === 'released' ? 'closed' : devStatus === 'fixed' || devStatus === 'verified' ? 'actioned' : devStatus === 'new' ? 'new' : 'reviewed',
    timeline: [...(f.timeline ?? []), { id: uid(), devStatus, timestamp: now, note }],
  };

  if (devStatus === 'fixed')    patch.fixedInBuild    = BETA_OPS_BUILD;
  if (devStatus === 'verified') patch.verifiedInBuild = BETA_OPS_BUILD;
  if (devStatus === 'released') patch.releasedInBuild = BETA_OPS_BUILD;
  if (assignedTo)               patch.assignedTo      = assignedTo;

  Object.assign(store.feedback[idx], patch);
  persist(store);
}

export function updateFeedback(id: string, patch: Partial<Pick<BetaFeedback, 'status' | 'bugId' | 'severity' | 'assignedTo' | 'popularity'>>): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === id);
  if (idx !== -1) Object.assign(store.feedback[idx], patch);
  persist(store);
}

export function addInternalNote(id: string, content: string): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === id);
  if (idx === -1) return;
  const note: InternalNote = { id: uid(), content, timestamp: new Date().toISOString() };
  store.feedback[idx].internalNotes = [...(store.feedback[idx].internalNotes ?? []), note];
  persist(store);
}

export function deleteInternalNote(feedbackId: string, noteId: string): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === feedbackId);
  if (idx === -1) return;
  store.feedback[idx].internalNotes = (store.feedback[idx].internalNotes ?? []).filter(n => n.id !== noteId);
  persist(store);
}

export function addAttachment(id: string, attachment: Omit<FeedbackAttachment, 'id' | 'timestamp'>): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === id);
  if (idx === -1) return;
  const att: FeedbackAttachment = { ...attachment, id: uid(), timestamp: new Date().toISOString() };
  store.feedback[idx].attachments = [...(store.feedback[idx].attachments ?? []), att];
  persist(store);
}

export function upvoteFeature(id: string): void {
  const store = load();
  const idx   = store.feedback.findIndex(f => f.id === id);
  if (idx === -1) return;
  store.feedback[idx].popularity = (store.feedback[idx].popularity ?? 0) + 1;
  persist(store);
}

export function deleteFeedback(id: string): void {
  const store  = load();
  store.feedback = store.feedback.filter(f => f.id !== id);
  persist(store);
}

export function clearAllFeedback(): void {
  persist({ feedback: [], lastUpdated: new Date().toISOString() });
}

// ─── Analytics ────────────────────────────────────────────────────────────────

const SEV_SCORE: Record<FeedbackSeverity, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const OPEN_STATUSES = new Set<FeedbackDevStatus>(['new', 'needs-investigation', 'assigned', 'in-progress', 'cannot-reproduce']);
const CLOSED_STATUSES = new Set<FeedbackDevStatus>(['fixed', 'verified', 'released', 'deferred', 'rejected', 'closed']);

export function getAnalytics(openBugsCount = 0, platformHealthScore = 87): BetaAnalytics {
  const feedback = load().feedback;
  const now      = Date.now();
  const week     = 7 * 24 * 60 * 60 * 1000;

  const byType: Record<FeedbackType, number>         = { bug: 0, feature: 0, improvement: 0, question: 0, positive: 0, rating: 0 };
  const bySeverity: Record<FeedbackSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  const byDevStatus: Record<FeedbackDevStatus, number> = {
    new: 0, 'needs-investigation': 0, assigned: 0, 'in-progress': 0,
    'cannot-reproduce': 0, fixed: 0, verified: 0, released: 0, deferred: 0, rejected: 0, closed: 0,
  };
  const reportsByBrowser: Record<string, number> = {};
  const reportsByDevice:  Record<string, number> = {};
  const reportsByModule:  Record<string, number> = {};
  const buildDist:        Record<string, number> = {};
  const testerSet = new Set<string>();
  const dailySet  = new Set<string>();

  const bugDescriptions: string[] = [], featureDescriptions: string[] = [];
  let ratingSum = 0, ratingCount = 0, recentCount = 0;
  let crashCount = 0, uiCount = 0, aiCount = 0, storageCount = 0, authCount = 0;
  let sevScoreSum = 0, sevScoreCount = 0;
  let openCount = 0, closedCount = 0, regressionCount = 0;
  let resolutionHoursSum = 0, resolutionCount = 0;
  const duplicateGroups = new Set<string>();

  for (const f of feedback) {
    byType[f.type]++;
    const sev = f.severity ?? 'medium';
    bySeverity[sev]++;
    const ds = f.devStatus ?? 'new';
    byDevStatus[ds]++;
    buildDist[f.buildNumber] = (buildDist[f.buildNumber] ?? 0) + 1;
    testerSet.add(`${f.browser}-${f.os}-${f.screenResolution}`);
    reportsByBrowser[f.browser] = (reportsByBrowser[f.browser] ?? 0) + 1;
    reportsByDevice[f.device]   = (reportsByDevice[f.device]   ?? 0) + 1;
    reportsByModule[f.currentModule] = (reportsByModule[f.currentModule] ?? 0) + 1;

    const age = now - new Date(f.timestamp).getTime();
    if (age < week) { recentCount++; dailySet.add(new Date(f.timestamp).toDateString()); }
    if (f.type === 'rating' && f.rating != null) { ratingSum += f.rating; ratingCount++; }
    if (f.type === 'bug')     bugDescriptions.push(f.title);
    if (f.type === 'feature') featureDescriptions.push(f.title);

    const text = (f.title + ' ' + f.description).toLowerCase();
    if (text.includes('crash') || text.includes('error'))    crashCount++;
    if (text.includes('ui') || text.includes('layout') || text.includes('display')) uiCount++;
    if (text.includes(' ai ') || text.includes('intel'))     aiCount++;
    if (text.includes('storage') || text.includes('upload') || text.includes('save')) storageCount++;
    if (text.includes('login') || text.includes('auth') || text.includes('sign'))   authCount++;

    if (f.type === 'bug') {
      sevScoreSum += SEV_SCORE[sev]; sevScoreCount++;
    }

    if (OPEN_STATUSES.has(ds))   openCount++;
    if (CLOSED_STATUSES.has(ds)) closedCount++;
    if (f.isRegression)          regressionCount++;
    if (f.duplicateGroupId)      duplicateGroups.add(f.duplicateGroupId);

    // Resolution time: from first timeline event to first fixed/closed event
    const tl = f.timeline ?? [];
    const submitted = tl.find(e => e.devStatus === 'new');
    const resolved  = tl.find(e => e.devStatus === 'fixed' || e.devStatus === 'closed' || e.devStatus === 'verified');
    if (submitted && resolved) {
      const hrs = (new Date(resolved.timestamp).getTime() - new Date(submitted.timestamp).getTime()) / 3_600_000;
      if (hrs >= 0) { resolutionHoursSum += hrs; resolutionCount++; }
    }
  }

  const sorted = Object.entries(reportsByModule).sort((a, b) => b[1] - a[1]);
  const allModules = ROUTE_MODULE_MAP.map(([, m]) => m);
  const problematic = sorted.slice(0, 3).map(([m]) => m);
  const stableModules = allModules.filter(m => !problematic.includes(m)).slice(0, 4);

  const bugCount = byType.bug;
  const crashRate          = bugCount > 0 ? Math.min(100, Math.round((crashCount / bugCount) * 100)) : 0;
  const aiSuccessRate      = feedback.length > 0 ? Math.max(0, 100 - Math.round((aiCount      / feedback.length) * 100)) : 100;
  const storageSuccessRate = feedback.length > 0 ? Math.max(0, 100 - Math.round((storageCount / feedback.length) * 100)) : 100;

  const topFeaturesByPopularity = feedback
    .filter(f => f.type === 'feature')
    .sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))
    .slice(0, 10)
    .map(f => ({ title: f.title, popularity: f.popularity ?? 0 }));

  return {
    totalFeedback:            feedback.length,
    byType,
    averageRating:            ratingCount > 0 ? Math.round((ratingSum / ratingCount) * 10) / 10 : null,
    openBugsCount,
    crashReports:             crashCount,
    uiIssues:                 uiCount,
    aiIssues:                 aiCount,
    storageIssues:            storageCount,
    authIssues:               authCount,
    topComplaints:            bugDescriptions.slice(-5).reverse(),
    topFeatureRequests:       featureDescriptions.slice(-5).reverse(),
    mostProblematicModules:   problematic.length > 0 ? problematic : ['None identified'],
    mostStableModules:        stableModules.length > 0 ? stableModules : ['All modules'],
    crashRate,
    aiSuccessRate,
    storageSuccessRate,
    buildVersionDistribution: buildDist,
    uniqueTesters:            testerSet.size,
    dailyActiveTesters:       Math.max(1, dailySet.size),
    recentFeedbackCount:      recentCount,
    platformHealthScore,
    // Sprint 098
    criticalCount:            bySeverity.critical,
    highCount:                bySeverity.high,
    mediumCount:              bySeverity.medium,
    lowCount:                 bySeverity.low,
    avgSeverityScore:         sevScoreCount > 0 ? Math.round((sevScoreSum / sevScoreCount) * 10) / 10 : 0,
    openIssuesCount:          openCount,
    closedIssuesCount:        closedCount,
    regressionCount,
    duplicateGroupCount:      duplicateGroups.size,
    avgResolutionTimeHours:   resolutionCount > 0 ? Math.round((resolutionHoursSum / resolutionCount) * 10) / 10 : null,
    crashFreeSessionsPct:     Math.max(0, 100 - crashRate),
    reportsByBrowser,
    reportsByDevice,
    reportsByModule,
    topFeaturesByPopularity,
    bySeverity,
    byDevStatus,
    avgResolutionDays:        resolutionCount > 0 ? Math.round((resolutionHoursSum / resolutionCount / 24) * 10) / 10 : null,
  };
}

// ─── Daily Summary ────────────────────────────────────────────────────────────

export function getDailySummary(): string {
  const all   = getAllFeedback();
  const today = new Date().toDateString();
  const todayItems = all.filter(f => new Date(f.timestamp).toDateString() === today);
  const fixedToday = all.filter(f => {
    const tl = f.timeline ?? [];
    return tl.some(e => (e.devStatus === 'fixed' || e.devStatus === 'closed') && new Date(e.timestamp).toDateString() === today);
  });
  const critical = todayItems.filter(f => f.severity === 'critical');
  const moduleCounts: Record<string, number> = {};
  for (const f of todayItems) { moduleCounts[f.currentModule] = (moduleCounts[f.currentModule] ?? 0) + 1; }
  const topModule = Object.entries(moduleCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'None';
  const sevSum    = todayItems.reduce((s, f) => s + SEV_SCORE[f.severity ?? 'medium'], 0);
  const avgSev    = todayItems.length > 0 ? (sevSum / todayItems.length).toFixed(1) : '0';

  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Daily Beta Summary
**Date:** ${date}
**Build:** ${BETA_OPS_BUILD}

---

## Today's Stats

| Metric | Value |
|--------|-------|
| New Reports Today | ${todayItems.length} |
| Fixed Today | ${fixedToday.length} |
| Critical Issues | ${critical.length} |
| Most Active Module | ${topModule} |
| Average Severity Score | ${avgSev} / 4.0 |
| Build Stability | ${Math.max(0, 100 - critical.length * 20)}% |

---

## Today's New Reports
${todayItems.length === 0 ? '- No reports submitted today.' : todayItems.map(f =>
  `- [${f.severity?.toUpperCase() ?? 'MEDIUM'}] [${f.type.toUpperCase()}] [${f.currentModule}] ${f.title}`).join('\n')}

## Fixed Today
${fixedToday.length === 0 ? '- No issues fixed today.' : fixedToday.map(f => `- ${f.title}`).join('\n')}

## Open Critical Issues
${critical.length === 0 ? '- No critical issues today.' : critical.map(f => `- [${f.currentModule}] ${f.title}: ${f.description?.slice(0, 100) ?? ''}`).join('\n')}

---
*WorkRoamer™ Beta Operations Center™ — Build ${BETA_OPS_BUILD}*
Paste into ChatGPT: "Review today's beta report. Identify urgent engineering priorities. Recommend actions for tomorrow's sprint."`;
}

// ─── Weekly Summary ───────────────────────────────────────────────────────────

export function getWeeklySummary(): string {
  const all   = getAllFeedback();
  const week  = 7 * 24 * 60 * 60 * 1000;
  const since = Date.now() - week;
  const weekItems = all.filter(f => new Date(f.timestamp).getTime() > since);
  const bugs     = weekItems.filter(f => f.type === 'bug');
  const features = weekItems.filter(f => f.type === 'feature');
  const fixed    = all.filter(f => f.devStatus === 'fixed' || f.devStatus === 'released');
  const regressions = weekItems.filter(f => f.isRegression);
  const ratings  = weekItems.filter(f => f.type === 'rating' && f.rating != null);
  const avgRating = ratings.length > 0 ? (ratings.reduce((s, f) => s + (f.rating ?? 0), 0) / ratings.length).toFixed(1) : 'N/A';
  const topBugs  = bugs.sort((a, b) => SEV_SCORE[b.severity ?? 'medium'] - SEV_SCORE[a.severity ?? 'medium']).slice(0, 5);
  const topFeatures = features.sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0)).slice(0, 5);
  const crashFree = bugs.length > 0 ? Math.max(0, Math.round((1 - bugs.filter(f => f.severity === 'critical').length / bugs.length) * 100)) : 100;

  // resolution time
  let resSum = 0, resCount = 0;
  for (const f of all) {
    const tl = f.timeline ?? [];
    const submitted = tl.find(e => e.devStatus === 'new');
    const resolved  = tl.find(e => e.devStatus === 'fixed' || e.devStatus === 'closed');
    if (submitted && resolved) {
      const days = (new Date(resolved.timestamp).getTime() - new Date(submitted.timestamp).getTime()) / 86_400_000;
      if (days >= 0) { resSum += days; resCount++; }
    }
  }
  const avgRes = resCount > 0 ? `${(resSum / resCount).toFixed(1)} days` : 'N/A';

  // Beta health score = weighted blend
  const healthScore = Math.max(0, Math.min(100,
    100 - (regressions.length * 15) - (bugs.filter(f => f.severity === 'critical').length * 10) - Math.max(0, bugs.length - 5)
  ));

  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Weekly Beta Summary
**Week Ending:** ${date}
**Build:** ${BETA_OPS_BUILD}

---

## Weekly Stats

| Metric | Value |
|--------|-------|
| New Reports (7d) | ${weekItems.length} |
| Bugs | ${bugs.length} |
| Feature Requests | ${features.length} |
| Fixed This Week | ${fixed.length} |
| Regressions | ${regressions.length} |
| Average Resolution Time | ${avgRes} |
| Crash-Free % | ${crashFree}% |
| Operator Satisfaction | ${avgRating}/5.0 |
| Beta Health Score | ${healthScore}/100 |

---

## Top Bugs This Week
${topBugs.length === 0 ? '- No bug reports this week.' : topBugs.map(f =>
  `- [${f.severity?.toUpperCase()}] [${f.currentModule}] ${f.title}${f.description ? `: ${f.description.slice(0, 80)}` : ''}`).join('\n')}

## Top Feature Requests
${topFeatures.length === 0 ? '- No feature requests this week.' : topFeatures.map(f =>
  `- [${f.popularity ?? 0} votes] ${f.title}`).join('\n')}

## Regression Alert
${regressions.length === 0 ? '- No regressions detected.' : regressions.map(f =>
  `- ⚠ REGRESSION: [${f.currentModule}] ${f.title}`).join('\n')}

---
*WorkRoamer™ Beta Operations Center™ — Build ${BETA_OPS_BUILD}*
Paste into ChatGPT: "Review this weekly beta summary. Assess platform stability. Recommend the next sprint priorities. Flag any concerning patterns."`;
}

// ─── Report generators ────────────────────────────────────────────────────────

export function generateReport(analytics: BetaAnalytics): string {
  const now     = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toISOString();
  const fb      = getAllFeedback();
  const bugs    = fb.filter(f => f.type === 'bug');
  const features = fb.filter(f => f.type === 'feature');
  const improvements = fb.filter(f => f.type === 'improvement');
  const positives = fb.filter(f => f.type === 'positive');

  function feedbackList(items: BetaFeedback[], fallback = 'None reported'): string {
    if (items.length === 0) return `  - ${fallback}`;
    return items.map(i => `  - [${i.severity?.toUpperCase() ?? 'MEDIUM'}] [${i.currentModule}] ${i.title}${i.description ? `: ${i.description.slice(0, 100)}` : ''}`).join('\n');
  }

  const ratingLine = analytics.averageRating != null
    ? `${'⭐'.repeat(Math.round(analytics.averageRating))} ${analytics.averageRating}/5.0`
    : 'No ratings submitted yet';

  return `# WorkRoamer™ Beta Operations Report
**Build:** ${BETA_OPS_BUILD}
**Date:** ${dateStr}
**Generated:** ${timeStr}

---

## Platform Health

| Metric | Value |
|--------|-------|
| Platform Health Score | ${analytics.platformHealthScore}% |
| Total Feedback | ${analytics.totalFeedback} |
| Open Issues | ${analytics.openIssuesCount} |
| Closed Issues | ${analytics.closedIssuesCount} |
| Critical Issues | ${analytics.criticalCount} |
| High Severity | ${analytics.highCount} |
| Regressions | ${analytics.regressionCount} |
| Duplicate Groups | ${analytics.duplicateGroupCount} |
| Avg Resolution | ${analytics.avgResolutionDays != null ? `${analytics.avgResolutionDays} days` : 'N/A'} |
| Crash-Free % | ${analytics.crashFreeSessionsPct}% |
| Satisfaction | ${ratingLine} |

---

## Severity Breakdown
| Severity | Count |
|----------|-------|
| 🔴 Critical | ${analytics.criticalCount} |
| 🟠 High | ${analytics.highCount} |
| 🟡 Medium | ${analytics.mediumCount} |
| ⚪ Low | ${analytics.lowCount} |

---

## Bug Summary
${feedbackList(bugs.filter(f => f.severity === 'critical'), 'No critical bugs')}

### All Bugs
${feedbackList(bugs)}

## Feature Requests (by popularity)
${feedbackList(features.sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0)))}

## Most Requested Improvements
${feedbackList(improvements)}

---

## Regression Report
${analytics.regressionCount === 0
  ? '  - No regressions detected.'
  : getAllFeedback().filter(f => f.isRegression).map(f => `  - ⚠ REGRESSION: [${f.currentModule}] ${f.title}`).join('\n')}

---

## Module Distribution
${Object.entries(analytics.reportsByModule).sort((a, b) => b[1] - a[1]).map(([m, n]) => `  - ${m}: ${n}`).join('\n') || '  - No data'}

## Browser Distribution
${Object.entries(analytics.reportsByBrowser).map(([b, n]) => `  - ${b}: ${n}`).join('\n') || '  - No data'}

## Device Distribution
${Object.entries(analytics.reportsByDevice).map(([d, n]) => `  - ${d}: ${n}`).join('\n') || '  - No data'}

---

## User Suggestions
${feedbackList(positives, 'No positive feedback yet')}

---

## Engineering Review Instructions

Paste this report into ChatGPT for an independent engineering review.

**Request:** Review this Beta Report. Identify patterns. Prioritize issues by severity and regression risk. Recommend bug fixes. Recommend UX improvements. Identify recurring user pain points. Generate the next recommended sprint.

---
*WorkRoamer™ Beta Operations Center™ — Build ${BETA_OPS_BUILD} — ${dateStr}*`;
}

export function exportCriticalIssues(): string {
  const items = getAllFeedback().filter(f => f.severity === 'critical');
  const date  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Critical Issues Report — ${date}\n**Build:** ${BETA_OPS_BUILD}\n**Total Critical:** ${items.length}\n\n---\n\n${
    items.length === 0 ? 'No critical issues.' :
    items.map(f => `## [CRITICAL] ${f.title}\n**Module:** ${f.currentModule} | **Status:** ${f.devStatus} | **Date:** ${new Date(f.timestamp).toLocaleDateString()}\n**Build:** ${f.reportedInBuild}\n\n${f.description ?? ''}\n\n**Context:** ${f.device} · ${f.browser} · ${f.os} · ${f.screenResolution}`).join('\n\n---\n\n')
  }\n\n---\nPaste into ChatGPT: "Review these critical issues. For each, propose a root cause and a fix. Prioritize by impact."`;
}

export function exportOpenIssues(): string {
  const items = getAllFeedback().filter(f => OPEN_STATUSES.has(f.devStatus ?? 'new') && f.type === 'bug');
  const date  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Open Issues — ${date}\n**Build:** ${BETA_OPS_BUILD}\n**Total Open:** ${items.length}\n\n---\n\n${
    items.length === 0 ? 'No open issues.' :
    items.map(f => `- [${f.severity?.toUpperCase()}] [${f.devStatus?.toUpperCase()}] [${f.currentModule}] ${f.title}${f.occurrenceCount > 1 ? ` (${f.occurrenceCount} occurrences)` : ''}${f.isRegression ? ' ⚠ REGRESSION' : ''}`).join('\n')
  }\n\n---\nPaste into ChatGPT: "Triage these open issues. Group by root cause. Recommend a fix order."`;
}

export function exportTodaysReports(): string {
  const today = new Date().toDateString();
  const items = getAllFeedback().filter(f => new Date(f.timestamp).toDateString() === today);
  const date  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Today's Reports — ${date}\n**Build:** ${BETA_OPS_BUILD}\n**Reports Today:** ${items.length}\n\n---\n\n${
    items.length === 0 ? 'No reports submitted today.' :
    items.map(f => `- [${f.type.toUpperCase()}] [${f.severity?.toUpperCase() ?? 'MEDIUM'}] ${f.title} — ${f.currentModule}`).join('\n')
  }\n\n---\nPaste into ChatGPT: "Review today's beta reports. What needs immediate attention?"`;
}

export function exportWeeklySummary(): string {
  return getWeeklySummary();
}

export function exportSelectedReports(ids: string[]): string {
  const all   = getAllFeedback();
  const items = ids.map(id => all.find(f => f.id === id)).filter(Boolean) as BetaFeedback[];
  const date  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return `# WorkRoamer™ Selected Reports — ${date}\n**Build:** ${BETA_OPS_BUILD}\n**Selected:** ${items.length}\n\n---\n\n${
    items.map(f => `## ${f.title}\n**Type:** ${f.type} | **Severity:** ${f.severity ?? 'medium'} | **Status:** ${f.devStatus} | **Module:** ${f.currentModule}\n\n${f.description ?? ''}`).join('\n\n---\n\n')
  }\n\n---\nPaste into ChatGPT: "Review these selected beta reports. Identify patterns and recommend fixes."`;
}

export function exportExecutiveSummary(analytics: BetaAnalytics): string {
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const healthEmoji = analytics.platformHealthScore >= 90 ? '🟢' : analytics.platformHealthScore >= 70 ? '🟡' : '🔴';
  return `# WorkRoamer™ Executive Beta Summary
**Date:** ${date} | **Build:** ${BETA_OPS_BUILD}

---

## Executive Overview

${healthEmoji} **Platform Health: ${analytics.platformHealthScore}%**

| | |
|--|--|
| Feedback Received | ${analytics.totalFeedback} |
| Open Issues | ${analytics.openIssuesCount} |
| Critical Bugs | ${analytics.criticalCount} |
| Regression Count | ${analytics.regressionCount} |
| Satisfaction | ${analytics.averageRating != null ? `${analytics.averageRating}/5.0` : 'N/A'} |
| Crash-Free Sessions | ${analytics.crashFreeSessionsPct}% |
| Avg Resolution | ${analytics.avgResolutionDays != null ? `${analytics.avgResolutionDays} days` : 'N/A'} |

## Risk Assessment

${analytics.criticalCount > 0 ? `🔴 **${analytics.criticalCount} critical issue(s) require immediate attention.**` : '🟢 No critical issues.'}
${analytics.regressionCount > 0 ? `⚠ **${analytics.regressionCount} regression(s) detected.**` : '✓ No regressions.'}

## Feature Pipeline
Top requested: ${analytics.topFeaturesByPopularity.slice(0, 3).map(f => f.title).join(', ') || 'No requests yet'}

---
Paste into ChatGPT: "Review this executive beta summary. Provide a Go/No-Go recommendation for production release and list the top 3 engineering priorities."`;
}

export function exportJSON(): string {
  const store = load();
  return JSON.stringify(
    { build: BETA_OPS_BUILD, exported: new Date().toISOString(), ...store },
    null, 2,
  );
}
